import { useRef, useState } from "react";
import { X } from "lucide-react";
import { api, type SetupState } from "../api";
import { ModelAccess } from "./ModelAccess";
import { bestaetigen, fehlerMelden, fehlerText, toast, useDialog } from "./verwaltung";

/** Nur als Dialog (eingebettet) gibt es Esc und Fokusführung. */
function DialogRahmen({ onClose, children }: { onClose?: () => void; children: React.ReactNode }) {
  const box = useRef<HTMLDivElement | null>(null);
  useDialog(box, () => onClose?.(), Boolean(onClose));
  return (
    <div className="sheet-backdrop">
      <div ref={box} className="sheet setup-sheet" role="dialog" aria-modal="true" aria-labelledby="setup-titel">{children}</div>
    </div>
  );
}

type Step = "zugang" | "modell" | "postfach" | "fertig";

interface Props {
  state: SetupState;
  /** true, wenn der Assistent nachträglich aus der App geöffnet wurde. */
  embedded?: boolean;
  onDone: () => void;
  onClose?: () => void;
}

export function SetupWizard({ state, embedded, onDone, onClose }: Props) {
  const firstStep: Step = state.canCreateFirstUser ? "zugang" : !state.llm.configured ? "modell" : "postfach";
  const [step, setStep] = useState<Step>(firstStep);
  const [accounts, setAccounts] = useState(state.accounts);

  const steps: { id: Step; label: string }[] = [
    ...(state.canCreateFirstUser ? [{ id: "zugang" as const, label: "Zugang" }] : []),
    { id: "modell", label: "Modell" },
    { id: "postfach", label: "Postfächer" },
    { id: "fertig", label: "Loslegen" },
  ];
  const index = steps.findIndex((s) => s.id === step);

  const inhalt = (
    <>
        <header className="setup-head">
          <span className="brand-mark" aria-hidden="true">M</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <b id="setup-titel">{embedded ? "Einrichtung" : "Maildeck einrichten"}</b>
            <ol className="setup-steps" aria-label={`Schritt ${index + 1} von ${steps.length}`}>
              {steps.map((s, i) => (
                <li key={s.id} className="setup-step" data-state={i < index ? "fertig" : i === index ? "aktiv" : "offen"}
                  aria-current={i === index ? "step" : undefined}>
                  {i + 1}. {s.label}
                </li>
              ))}
            </ol>
          </div>
          {embedded && onClose && (
            <button className="icon-btn" onClick={onClose} aria-label="Einrichtung schließen" title="Schließen (Esc)"><X aria-hidden="true" /></button>
          )}
        </header>

        <div className="setup-body">
          {step === "zugang" && <ZugangStep onNext={() => setStep("modell")} />}
          {step === "modell" && (
            <ModelAccess
              current={state.llm}
              onSaved={() => setStep("postfach")}
              onSkip={() => setStep("postfach")}
            />
          )}
          {step === "postfach" && (
            <PostfachStep
              accounts={accounts}
              onAdded={(a) => setAccounts((prev) => [...prev, a])}
              onRemoved={(id) => setAccounts((prev) => prev.filter((x) => x.id !== id))}
              onNext={() => setStep("fertig")}
            />
          )}
          {step === "fertig" && <FertigStep accounts={accounts.length} onDone={onDone} />}
        </div>
    </>
  );

  return embedded
    ? <DialogRahmen onClose={onClose}>{inhalt}</DialogRahmen>
    : <div className="setup-wrap"><div className="setup">{inhalt}</div></div>;
}

// ─── Schritt 1: Zugang ────────────────────────────────────────────────────────

function ZugangStep({ onNext }: { onNext: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== repeat) return setError("Die beiden Passwörter stimmen nicht überein.");
    setBusy(true);
    setError(null);
    try {
      await api.createFirstUser(username, password);
      onNext();
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <h2>Dein Zugang</h2>
      <p className="lede">
        Damit meldest du dich an dieser Oberfläche an — nicht bei deinem Mailanbieter. Wähle etwas
        anderes als dein Postfach-Passwort.
      </p>

      <div className="field">
        <label htmlFor="su">Benutzername</label>
        <input id="su" className="input" autoFocus autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="manu" />
      </div>
      <div className="cond" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div className="field">
          <label htmlFor="sp">Passwort</label>
          <input id="sp" className="input" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="sp2">Wiederholen</label>
          <input id="sp2" className="input" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
        </div>
      </div>
      <p className="hint" style={{ marginTop: -8 }}>
        Mindestens 10 Zeichen{password.length > 0 && password.length < 10 ? ` — noch ${10 - password.length}` : ""}.
        {repeat && password !== repeat && <span style={{ color: "var(--danger)" }}> Die Wiederholung stimmt noch nicht überein.</span>}
      </p>

      {error && <p className="error">{error}</p>}

      <div className="setup-foot">
        <button className="btn primary" disabled={busy || username.trim().length < 3 || password.length < 10 || password !== repeat}>
          {busy ? "Wird angelegt …" : "Weiter"}
        </button>
      </div>
    </form>
  );
}

// ─── Schritt 2: Modellzugang ──────────────────────────────────────────────────

// ─── Schritt 3: Postfächer ────────────────────────────────────────────────────

interface AccountSummary { id: number; name: string; email: string; active: boolean }

function PostfachStep({
  accounts, onAdded, onRemoved, onNext,
}: {
  accounts: AccountSummary[];
  onAdded: (a: AccountSummary) => void;
  onRemoved: (id: number) => void;
  onNext: () => void;
}) {
  const [adding, setAdding] = useState(accounts.length === 0);

  return (
    <div>
      <h2>Postfächer</h2>
      <p className="lede">
        Du kannst mehrere Konten verbinden — der Agent sortiert jedes einzeln und hält die Ordner
        getrennt. Die Zugangsdaten werden verschlüsselt gespeichert.
      </p>

      {accounts.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {accounts.map((a) => (
            <div className="account-row" key={a.id}>
              <span className="state-dot" data-state="da" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{a.email}</b>
                <div className="hint" style={{ margin: 0 }}>{a.name}</div>
              </div>
              <button
                className="btn quiet"
                onClick={async () => {
                  const ja = await bestaetigen({
                    titel: `${a.email} entfernen?`,
                    text: "Die gespeicherten Zugangsdaten werden gelöscht. Auf dem Mailserver ändert sich nichts.",
                    bestaetigen: "Entfernen",
                    gefahr: true,
                  });
                  if (!ja) return;
                  try {
                    await api.deleteAccount(a.id);
                    onRemoved(a.id);
                    toast.erfolg(`${a.email} entfernt.`);
                  } catch (e) {
                    fehlerMelden(e, "Entfernen fehlgeschlagen");
                  }
                }}
              >
                Entfernen
              </button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <AccountForm
          onCancel={accounts.length > 0 ? () => setAdding(false) : undefined}
          onSaved={(a) => { onAdded(a); setAdding(false); }}
        />
      ) : (
        <button className="btn" onClick={() => setAdding(true)}>Weiteres Postfach verbinden</button>
      )}

      {!adding && (
        <div className="setup-foot">
          <button className="btn primary" disabled={accounts.length === 0} onClick={onNext}>Weiter</button>
        </div>
      )}
    </div>
  );
}

type TestResult = Awaited<ReturnType<typeof api.testAccount>>;

function AccountForm({ onSaved, onCancel }: { onSaved: (a: AccountSummary) => void; onCancel?: () => void }) {
  const [form, setForm] = useState({
    email: "", name: "", imapHost: "", imapPort: 993, imapUser: "",
    imapPass: "", smtpHost: "", smtpPort: 587, signature: "",
  });
  const [test, setTest] = useState<TestResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Aus der Adresse die üblichen Servernamen vorschlagen. Läuft bei jeder
   * Änderung, sobald die Adresse vollständig aussieht — auf das Verlassen des
   * Feldes zu warten fühlt sich an, als passiere nichts.
   */
  const suggestHosts = (email: string) => {
    const domain = email.split("@")[1];
    if (!domain || !domain.includes(".")) return;
    setForm((f) => ({
      ...f,
      name: f.name || (email.split("@")[0] ?? ""),
      imapUser: f.imapUser || email,
      imapHost: f.imapHost || `imap.${domain}`,
      smtpHost: f.smtpHost || `smtp.${domain}`,
    }));
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setForm((f) => ({ ...f, email }));
    suggestHosts(email);
  };

  const payload = () => ({
    ...form,
    smtpHost: form.smtpHost || null,
    smtpPort: form.smtpPort || null,
    signature: form.signature || null,
  });

  const runTest = async () => {
    setBusy(true);
    setError(null);
    setTest(null);
    try {
      setTest(await api.testAccount(payload()));
    } catch (err) {
      setError(`Test fehlgeschlagen: ${fehlerText(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.createAccount(payload());
      toast.erfolg(`${form.email} verbunden — ${result.folders} ${result.folders === 1 ? "Ordner" : "Ordner"} gefunden.`);
      onSaved({ id: result.id, name: form.name, email: form.email, active: true });
    } catch (err) {
      setError(`Nicht gespeichert: ${fehlerText(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: k.endsWith("Port") ? (e.target.value === "" ? 0 : Number(e.target.value)) : e.target.value }));
  // Nach einer Änderung gilt ein früherer Test nicht mehr.
  const setUndNeuTesten = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => { setTest(null); set(k)(e); };

  const kannTesten = Boolean(form.email && form.imapHost && form.imapPass && form.imapPort);

  return (
    <form className="account-form" autoComplete="off"
      onSubmit={(e) => { e.preventDefault(); if (busy) return; if (test?.imap.ok) void save(); else if (kannTesten) void runTest(); }}>
      {/* autoComplete="off" überall: Chrome hält das sonst für ein Anmeldeformular
          und füllt gespeicherte Zugangsdaten fremder Konten ein. */}
      <div className="v-raster" style={{ gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)" }}>
        <div className="field">
          <label htmlFor="ak-mail">E-Mail-Adresse</label>
          <input id="ak-mail" className="input" type="email" autoComplete="off" name="mailkonto-adresse" autoFocus value={form.email}
            onChange={(e) => { setTest(null); onEmailChange(e); }} onBlur={() => suggestHosts(form.email)} placeholder="support@deine-domain.de" />
        </div>
        <div className="field">
          <label htmlFor="ak-name">Anzeigename</label>
          <input id="ak-name" className="input" autoComplete="off" name="mailkonto-name" value={form.name} onChange={set("name")} placeholder="Manu" />
        </div>
      </div>

      <fieldset className="v-editor-gruppe" style={{ border: 0, padding: 0, margin: "4px 0 0" }}>
        <legend className="v-gruppe-titel">Posteingang (IMAP)</legend>
        <div className="v-raster" data-spalten="3" style={{ gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 2fr)" }}>
          <div className="field">
            <label htmlFor="ak-imap">Server</label>
            <input id="ak-imap" className="input" autoComplete="off" name="mailkonto-imap" value={form.imapHost} onChange={setUndNeuTesten("imapHost")} placeholder="imap.deine-domain.de" />
          </div>
          <div className="field">
            <label htmlFor="ak-imapport">Port</label>
            <input id="ak-imapport" className="input" type="number" min={1} max={65535} value={form.imapPort || ""} onChange={setUndNeuTesten("imapPort")} />
          </div>
          <div className="field">
            <label htmlFor="ak-user">Benutzername</label>
            <input id="ak-user" className="input" autoComplete="off" name="mailkonto-benutzer" value={form.imapUser} onChange={setUndNeuTesten("imapUser")} />
          </div>
        </div>
        <div className="field">
          <label htmlFor="ak-pass">Passwort</label>
          <input id="ak-pass" className="input" type="password" autoComplete="new-password" name="mailkonto-geheim" value={form.imapPass} onChange={setUndNeuTesten("imapPass")} />
          <p className="hint">Bei Gmail und Microsoft 365 ein App-Passwort, nicht das normale Kennwort.</p>
        </div>
      </fieldset>

      <fieldset className="v-editor-gruppe" style={{ border: 0, padding: 0, margin: "4px 0 0" }}>
        <legend className="v-gruppe-titel">Postausgang (SMTP) <span className="v-optional" style={{ fontWeight: 400 }}>— ohne ihn kein Versand</span></legend>
        <div className="v-raster" style={{ gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)" }}>
          <div className="field">
            <label htmlFor="ak-smtp">Server</label>
            <input id="ak-smtp" className="input" autoComplete="off" name="mailkonto-smtp" value={form.smtpHost} onChange={setUndNeuTesten("smtpHost")} placeholder="smtp.deine-domain.de" />
          </div>
          <div className="field">
            <label htmlFor="ak-smtpport">Port</label>
            <input id="ak-smtpport" className="input" type="number" min={1} max={65535} value={form.smtpPort || ""} onChange={setUndNeuTesten("smtpPort")} />
          </div>
        </div>
        <p className="hint" style={{ marginTop: -6 }}>IMAP ist fast immer Port 993, SMTP 587 oder 465. Benutzer und Passwort gelten für beide.</p>
      </fieldset>

      {test && (
        <div className="test-result" role="status">
          <div className={test.imap.ok ? "ok-line" : "fail-line"}>
            Posteingang: {test.imap.ok ? `verbunden, ${test.imap.folders.length} Ordner gefunden` : test.imap.error}
          </div>
          <div className={test.smtp.skipped ? "hint" : test.smtp.ok ? "ok-line" : "fail-line"}>
            Postausgang: {test.smtp.skipped ? "kein Server angegeben — Versenden bleibt aus" : test.smtp.ok ? "verbunden" : test.smtp.error}
          </div>
          {test.imap.ok && test.imap.folders.length > 0 && (
            <div className="folder-list">
              {test.imap.folders.slice(0, 14).map((f) => (
                <span className="chip" key={f.path}>{f.path}</span>
              ))}
              {test.imap.folders.length > 14 && <span className="hint">und {test.imap.folders.length - 14} weitere</span>}
            </div>
          )}
        </div>
      )}

      {error && <p className="error" role="alert">{error}</p>}

      <div className="setup-foot">
        <button type="button" className={test?.imap.ok ? "btn" : "btn primary"} disabled={busy || !kannTesten} onClick={() => void runTest()}>
          {busy && !test ? "Verbinde …" : test ? "Erneut testen" : "Verbindung testen"}
        </button>
        <button type="button" className={test?.imap.ok ? "btn primary" : "btn"} disabled={busy || !test?.imap.ok} onClick={() => void save()}>
          {busy && test ? "Speichert …" : "Postfach hinzufügen"}
        </button>
        {onCancel && <button type="button" className="btn quiet" onClick={onCancel}>Abbrechen</button>}
      </div>
      {!test && <p className="hint">Gespeichert wird erst, wenn die Verbindung zum Posteingang klappt.</p>}
      {test && !test.imap.ok && <p className="hint">Erst wenn der Posteingang verbindet, lässt sich das Postfach speichern.</p>}
    </form>
  );
}

// ─── Schritt 4: Loslegen ──────────────────────────────────────────────────────

function FertigStep({ accounts, onDone }: { accounts: number; onDone: () => void }) {
  const [testMode, setTestMode] = useState(true);
  const [busy, setBusy] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const finish = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.completeSetup(testMode);
      onDone();
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h2>Wie soll der Agent anfangen?</h2>
      <p className="lede">
        {accounts === 1 ? "Ein Postfach ist verbunden." : `${accounts} Postfächer sind verbunden.`} Der
        Agent liest ab jetzt mit und ordnet jede eingehende Mail ein.
      </p>

      <label className="choice" data-selected={testMode}>
        <input type="radio" name="setup-start" checked={testMode} onChange={() => setTestMode(true)} />
        <span>
          <b>Erst mitlesen</b>
          <span className="hint">
            Er schlägt vor und protokolliert, verschiebt aber nichts. Nach ein paar Tagen siehst du unter
            Auswertung, wie gut er trifft. Empfohlen.
          </span>
        </span>
      </label>

      <label className="choice" data-selected={!testMode}>
        <input type="radio" name="setup-start" checked={!testMode} onChange={() => setTestMode(false)} />
        <span>
          <b>Sofort sortieren</b>
          <span className="hint">
            Er verschiebt und markiert selbstständig, sobald er sich sicher genug ist. Jede Aktion bleibt
            im Protokoll und lässt sich zurücknehmen.
          </span>
        </span>
      </label>

      {error && <p className="error" role="alert">{error}</p>}
      <div className="setup-foot">
        <button className="btn primary" disabled={busy} onClick={finish}>
          {busy ? "Einen Moment …" : "Los geht's"}
        </button>
      </div>

      <p className="hint" style={{ marginTop: 14 }}>
        Damit die Postfächer laufend abgerufen werden, muss der Worker laufen:{" "}
        <code className="mono">npm run dev:worker</code> — im Docker-Betrieb übernimmt das der Dienst
        <code className="mono">worker</code> von selbst.
      </p>
    </div>
  );
}
